import {
  useRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { ApiLoginResult } from "@tm-food/api-contract";
import { isApiClientError } from "@/shared/api/errors";
import { createHttpClient, type HttpClient } from "@/shared/api/http";
import {
  createBrowserTokenStore,
  setRememberedUsername,
} from "@/shared/auth/tokenStore";
import { mapUserProfile, type AuthUser } from "@/shared/auth/types";
import { createContext, useContext } from "react";

type AuthStatus = "bootstrapping" | "anonymous" | "authenticated";

export type LoginOptions = {
  plantCode?: string | null;
  /** Persist session across browser restarts. Default true. */
  rememberMe?: boolean;
};

type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  http: HttpClient;
  login: (
    username: string,
    password: string,
    options?: LoginOptions | string | null,
  ) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeLoginOptions(
  options?: LoginOptions | string | null,
): LoginOptions {
  if (options == null) return {};
  if (typeof options === "string") return { plantCode: options };
  return options;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const tokenStore = useMemo(() => createBrowserTokenStore(), []);
  const userRef = useRef<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("bootstrapping");
  const [user, setUser] = useState<AuthUser | null>(null);

  const http = useMemo(
    () =>
      createHttpClient({
        tokenStore,
        getPlantCode: () => userRef.current?.plantCode ?? null,
        onUnauthorized: () => {
          userRef.current = null;
          setUser(null);
          setStatus("anonymous");
        },
      }),
    [tokenStore],
  );

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const tokens = tokenStore.get();
      if (!tokens?.accessToken) {
        if (!cancelled) {
          setStatus("anonymous");
          setUser(null);
        }
        return;
      }

      let delayMs = 0;
      while (!cancelled) {
        if (delayMs > 0) await new Promise((resolve) => setTimeout(resolve, delayMs));
        if (cancelled) return;

        try {
          const me = await http.me();
          if (!cancelled) {
            const mapped = mapUserProfile(me);
            userRef.current = mapped;
            setUser(mapped);
            setStatus("authenticated");
          }
          return;
        } catch (error) {
          const rejected = isApiClientError(error) && error.status === 401;
          if (rejected || !tokenStore.get()?.refreshToken) {
            tokenStore.clear();
            if (!cancelled) {
              userRef.current = null;
              setUser(null);
              setStatus("anonymous");
            }
            return;
          }
          delayMs = delayMs === 0 ? 1000 : Math.min(delayMs * 2, 8000);
        }
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [http, tokenStore]);

  const login = useCallback(
    async (
      username: string,
      password: string,
      options?: LoginOptions | string | null,
    ) => {
      const opts = normalizeLoginOptions(options);
      const rememberMe = opts.rememberMe ?? true;
      const result: ApiLoginResult = await http.login(
        username,
        password,
        opts.plantCode,
      );
      tokenStore.set(
        {
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
          accessTokenExpiresAt: result.accessTokenExpiresAt,
        },
        { persist: rememberMe },
      );
      if (rememberMe) {
        setRememberedUsername(username);
      } else {
        setRememberedUsername(null);
      }
      const mapped = mapUserProfile(result.user);
      userRef.current = mapped;
      setUser(mapped);
      setStatus("authenticated");
      return mapped;
    },
    [http, tokenStore],
  );

  const logout = useCallback(async () => {
    await http.logout();
    userRef.current = null;
    setUser(null);
    setStatus("anonymous");
  }, [http]);

  const refreshProfile = useCallback(async () => {
    const me = await http.me();
    const mapped = mapUserProfile(me);
    userRef.current = mapped;
    setUser(mapped);
    setStatus("authenticated");
  }, [http]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      http,
      login,
      logout,
      refreshProfile,
    }),
    [status, user, http, login, logout, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
